import { useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/videos";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const features = [
  {
    icon: "ri-hd-line",
    title: "高清流畅观看",
    desc: "适配多端的播放体验，支持高清画质与稳定播放，让每一次观看都顺畅舒适。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "丰富内容分类",
    desc: "短视频、影视剧、综艺、动漫、纪录片等多个频道，按兴趣快速找到想看的内容。",
  },
  {
    icon: "ri-fire-line",
    title: "热度排行推荐",
    desc: "结合播放量与更新时间的排行与推荐，让你不错过每一个热门精彩瞬间。",
  },
  {
    icon: "ri-search-eye-line",
    title: "快速搜索发现",
    desc: "支持按关键词、类型筛选内容，输入即匹配，轻松定位你感兴趣的视频。",
  },
];

const usage = [
  "本站所有视频内容均为演示用虚构示例，标题、封面与简介仅用于界面展示，不构成任何真实版权作品。",
  "本平台为前端演示网站，视频播放为占位演示状态，未接入真实视频流接口，不提供真实视频播放与下载功能。",
  "收藏功能基于浏览器本地存储实现，数据仅保存在你的设备中，清除浏览器数据后收藏记录将丢失。",
  "用户在使用本平台时应遵守相关法律法规，尊重内容版权，不进行任何侵权或违规行为。",
];

export default function AboutPage() {
  const [qrOpen, setQrOpen] = useState(false);

  useDocumentMeta(
    "关于西瓜视频",
    "了解西瓜视频平台定位、内容范围与服务特色，获取邮箱、微信公众号等联系方式，并阅读平台使用说明与版权声明。",
    "关于西瓜视频,西瓜视频联系方式,视频平台介绍,版权声明,用户须知"
  );

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-8">
        <div className="rounded-lg bg-background-100 border border-background-200 px-6 md:px-12 py-10 md:py-14">
          <span className="inline-block px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-4">
            ABOUT US
          </span>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
            关于西瓜视频
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            西瓜视频是一个以在线视频播放为核心的内容平台，致力于为广泛的互联网用户提供高清流畅的视频观看体验
            与丰富多样的内容选择。无论是碎片时间刷短视频，还是周末追一部影视剧、看一档综艺，这里都能找到合适的内容。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div>
            <h2 className="text-lg md:text-2xl font-bold font-heading text-foreground-900 mb-4">
              平台使命
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              我们相信好的内容应当被更多人看见。西瓜视频希望通过清晰的内容组织、便捷的查找方式与舒适的观看体验，
              让每一位用户都能轻松地发现、浏览并享受喜欢的视频内容，把碎片时间变成愉悦的时光。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4">
              面向喜欢短视频、影视剧、综艺节目及其他多样视频内容的用户群体，我们持续丰富内容品类，
              并借助分类、标签、热度排行与个性化推荐，帮助用户高效地找到感兴趣的内容。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { value: "120万+", label: "在线视频内容" },
              { value: "8 大", label: "内容分类频道" },
              { value: "每日", label: "更新最新内容" },
              { value: "多端", label: "高清流畅体验" },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-lg bg-accent-50 border border-accent-100 px-5 py-6"
              >
                <p className="text-2xl md:text-3xl font-bold font-heading text-primary-600">
                  {item.value}
                </p>
                <p className="text-sm text-foreground-500 mt-1.5">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-100 border-y border-background-200">
        <h2 className="text-lg md:text-2xl font-bold font-heading text-foreground-900 mb-2">
          内容范围
        </h2>
        <p className="text-sm text-foreground-500 mb-6">从短视频到影视剧，覆盖多种内容类型</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.id}`}
              className="group rounded-lg bg-background-50 border border-background-200 px-4 py-5 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className="w-10 h-10 rounded-lg bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 flex items-center justify-center mb-3 transition-colors">
                <i className={`${cat.icon} text-xl`}></i>
              </span>
              <h3 className="text-sm font-bold text-foreground-900 mb-1">{cat.name}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{cat.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <h2 className="text-lg md:text-2xl font-bold font-heading text-foreground-900 mb-6">
          服务特色
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {features.map((f) => (
            <div key={f.title} className="rounded-lg bg-background-100 border border-background-200 p-5">
              <span className="w-11 h-11 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center mb-4">
                <i className={`${f.icon} text-2xl`}></i>
              </span>
              <h3 className="text-base font-bold text-foreground-900 mb-2">{f.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-lg md:text-2xl font-bold font-heading text-foreground-900 mb-4">
              使用说明与版权声明
            </h2>
            <ul className="space-y-3">
              {usage.map((text) => (
                <li key={text} className="flex items-start gap-3 text-sm text-foreground-600 leading-relaxed">
                  <i className="ri-checkbox-circle-line text-primary-500 text-lg mt-0.5 shrink-0"></i>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h2 className="text-lg font-bold font-heading text-foreground-900 mb-4">联系我们</h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-md bg-background-50 text-primary-500 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="text-foreground-400 text-xs mb-0.5">合作邮箱</p>
                  <a
                    href="mailto:hello@xigua-video.example.com"
                    className="text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer break-all"
                  >
                    hello@xigua-video.example.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-md bg-background-50 text-primary-500 flex items-center justify-center shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </span>
                <div>
                  <p className="text-foreground-400 text-xs mb-0.5">客服热线</p>
                  <a
                    href="tel:4008001234"
                    className="text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    400-800-1234
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-md bg-background-50 text-primary-500 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <div>
                  <p className="text-foreground-400 text-xs mb-0.5">公司地址</p>
                  <p className="text-foreground-800">北京市朝阳区视频大厦 8 层</p>
                </div>
              </li>
            </ul>

            <div className="mt-6 pt-5 border-t border-background-200 text-center">
              <button
                type="button"
                onClick={() => setQrOpen(true)}
                className="inline-flex flex-col items-center gap-2 cursor-pointer group"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20minimal%20stylized%20QR%20code%20graphic%20on%20a%20white%20background%20with%20subtle%20green%20and%20black%20square%20pattern%2C%20neat%20grid%2C%20high%20detail%20vector%20style&width=320&height=320&seq=xg-wechat-qr&orientation=squarish"
                  alt="西瓜视频微信公众号二维码"
                  title="西瓜视频微信公众号"
                  className="w-28 h-28 rounded-md border border-background-200 bg-background-50"
                />
                <span className="text-xs text-foreground-500 group-hover:text-primary-600 transition-colors">
                  微信公众号 · 点击放大
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {qrOpen && (
        <div
          className="fixed inset-0 z-[60] bg-foreground-950/70 flex items-center justify-center px-4"
          onClick={() => setQrOpen(false)}
        >
          <div
            className="bg-background-50 rounded-lg p-6 max-w-xs w-full text-center xg-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20minimal%20stylized%20QR%20code%20graphic%20on%20a%20white%20background%20with%20subtle%20green%20and%20black%20square%20pattern%2C%20neat%20grid%2C%20high%20detail%20vector%20style&width=320&height=320&seq=xg-wechat-qr&orientation=squarish"
              alt="西瓜视频微信公众号二维码"
              title="西瓜视频微信公众号"
              className="w-full aspect-square rounded-md border border-background-200"
            />
            <p className="mt-3 text-sm text-foreground-700">扫码关注「西瓜视频」微信公众号</p>
            <button
              type="button"
              onClick={() => setQrOpen(false)}
              className="mt-4 w-full py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
            >
              关闭
            </button>
          </div>
        </div>
      )}
    </div>
  );
}